import Reveal from "@/components/Reveal";
import { Zap, Building2, Coins, Activity, Sparkles, Shield, Users as UsersIco, Crown, Map, Star, Lock, Trophy, Globe } from "lucide-react";
import { TID } from "@/lib/testIds";
import { useSiteContent } from "@/admin/SiteContent";

const ICONS = { Zap, Building2, Coins, Activity, Sparkles, Shield, Users: UsersIco, Crown, Map, Star, Lock, Trophy, Globe };

const LORE_DEFAULT = [
  { key: "city", icon: "Zap", title: "Los Santos sotto pressione", desc: "La città vive una frattura continua: quartieri in trasformazione, potere fragile e notti dove ogni scelta può diventare reputazione." },
  { key: "institutions", icon: "Building2", title: "Istituzioni in bilico", desc: "Dipartimenti, politica e servizi pubblici provano a tenere insieme ordine e fiducia mentre la strada spinge in direzione opposta." },
  { key: "economy", icon: "Coins", title: "Economie parallele", desc: "Lavoro legale, debiti, favori e mercati nascosti costruiscono una rete dove il denaro conta, ma la reputazione pesa di più." },
  { key: "consequences", icon: "Activity", title: "Conseguenze giocabili", desc: "Ogni personaggio lascia tracce: alleanze, errori, ambizioni e ferite diventano materiale vivo per nuove scene." },
];

export const Features = () => {
  const { content } = useSiteContent();
  const LORE = (content?.features && content.features.length > 0
    ? content.features
    : LORE_DEFAULT
  ).map((f, i) => ({ ...f, key: f.key || `feat-${i}` }));
  return (
    <section
      id="features"
      data-testid={TID.features.root}
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Big visual lore banner — 5starsrp style */}
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl card-wow min-h-[300px] sm:min-h-[420px] mb-16">
            <img
              src="https://images.unsplash.com/photo-1521207418485-99c705420785?auto=format&fit=crop&w=1800&q=75"
              alt="Lore"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-50"
              style={{ filter: "contrast(1.05) saturate(0.85) brightness(0.55)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0F] via-[#0B0B0F]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0F] via-transparent to-transparent" />

            {/* Star pattern subtle */}
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "radial-gradient(circle, #fff 1.5px, transparent 1.5px)",
                backgroundSize: "60px 60px",
              }}
            />

            <div className="relative h-full flex flex-col items-center justify-center text-center px-6 py-16 sm:py-24">
              <span className="font-mono-pl text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] mb-6">
                Lore Ufficiale
              </span>
              <h2 className="font-display text-5xl sm:text-7xl lg:text-8xl uppercase text-[#F5F5F5] leading-[0.92] tracking-wide glow-gold-strong">
                L&apos;eco di <span className="text-gold">Los Santos</span>
              </h2>
              <p className="mt-8 max-w-2xl text-[#F5F5F5]/85 text-base sm:text-lg leading-relaxed">
                Istituzioni sotto pressione. Economie parallele. Ambizioni
                senza limiti. Benvenuto nella nuova era del roleplay.
              </p>
            </div>
          </div>
        </Reveal>

        {/* features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {LORE.map((f, i) => {
            const Icon = ICONS[f.icon] || Sparkles;
            return (
              <Reveal key={f.key} delay={(i % 4) + 1}>
                <article
                  data-testid={TID.features.card(f.key)}
                  className="card-wow rounded-2xl p-8 h-full flex flex-col"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#0B0B0F]/60 border border-[#00D4FF]/30 flex items-center justify-center">
                    <Icon size={20} className="text-[#00D4FF]" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl uppercase font-normal text-[#F5F5F5] mt-10 tracking-wide">
                    {f.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#B6B6BE]">
                    {f.desc}
                  </p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
